import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { contactsAPI, companiesAPI, segmentsAPI, productLinesAPI, usersAPI, voipAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../components/ui/popover';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Plus, Search, Mail, Phone, PhoneCall, Building, Edit, Trash2, User, Eye, Filter, X, Sparkles, ArrowUpDown, TrendingUp, TrendingDown, Minus, Calendar, Settings, Package, CheckSquare, MoreHorizontal, UserX, UserCheck, UserMinus, FileSpreadsheet, UserPlus, Tags, Loader2, Globe, ShoppingCart, Target } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// AI Score filter options
const AI_SCORE_FILTERS = [
  { value: 'all', label: 'All Scores' },
  { value: 'high', label: 'High (80+)', icon: TrendingUp, color: 'text-emerald-600' },
  { value: 'medium', label: 'Medium (50-80)', icon: Minus, color: 'text-amber-600' },
  { value: 'low', label: 'Low (<50)', icon: TrendingDown, color: 'text-red-600' },
  { value: 'none', label: 'No Score', icon: Sparkles, color: 'text-slate-400' }
];

// Contact Status options - Active first, then Leads, then others
const CONTACT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'lead', label: 'Lead', color: 'bg-blue-100 text-blue-700' },
  { value: 'inactive', label: 'Inactive', color: 'bg-amber-100 text-amber-700' },
  { value: 'prospect', label: 'Prospect', color: 'bg-purple-100 text-purple-700' },
  { value: 'all', label: 'All Active Statuses' },
  { value: 'disabled', label: 'Disabled', color: 'bg-red-100 text-red-700' },
  { value: 'all_including_disabled', label: 'Show All (incl. Disabled)' }
];

export const ContactsPage = () => {
  const { hasRole } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [customerSegments, setCustomerSegments] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;
  const loadingPageRef = useRef(-1); // Track which page is currently being loaded
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [aiScoreFilter, setAiScoreFilter] = useState('all');
  const [segmentFilters, setSegmentFilters] = useState([]);
  const [productLineFilters, setProductLineFilters] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  // Bulk selection
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  // Bulk assignment dialog
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [bulkAssignData, setBulkAssignData] = useState({
    assigned_to: '',
    segments: [],
    product_lines: [],
    mode: 'add'
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [segmentDialog, setSegmentDialog] = useState(false);
  const [productLineDialog, setProductLineDialog] = useState(false);
  const [newSegment, setNewSegment] = useState({ value: '', label: '', color: 'bg-slate-100 text-slate-700' });
  const [newProductLine, setNewProductLine] = useState({ name: '', color: 'bg-slate-100 text-slate-700', description: '' });
  // Inline company creation
  const [showNewCompanyForm, setShowNewCompanyForm] = useState(false);
  const [newCompanyData, setNewCompanyData] = useState({
    name: '',
    industry: '',
    website: '',
    phone: '',
    address: '',
    city: '',
    country: ''
  });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_id: '',
    job_title: '',
    status: 'active',
    source: '',
    notes: '',
    segments: [],
    product_lines: [],
    contact_type: 'prospect',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    address_country: ''
  });

  useEffect(() => {
    // Reset and fetch when filters change
    setContacts([]);
    setPage(0);
    setHasMore(true);
    fetchData(0, true);
    fetchSegments();
    fetchProductLines();
    fetchTeamMembers();
  }, [statusFilter, aiScoreFilter, sortBy, sortOrder, productLineFilters]);

  const fetchTeamMembers = async () => {
    try {
      const res = await usersAPI.getTeam();
      setTeamMembers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch team members');
    }
  };

  const fetchSegments = async () => {
    try {
      const res = await segmentsAPI.getAll();
      setCustomerSegments(res.data.segments || []);
    } catch (error) {
      console.error('Failed to fetch segments');
    }
  };

  const fetchProductLines = async () => {
    try {
      const res = await productLinesAPI.getAll();
      setProductLines(res.data || []);
    } catch (error) {
      console.error('Failed to fetch product lines');
    }
  };

  const handleCreateSegment = async (e) => {
    e.preventDefault();
    if (!newSegment.value || !newSegment.label) {
      toast.error('Please enter segment value and label');
      return;
    }
    try {
      await segmentsAPI.create(newSegment);
      toast.success('Segment created');
      setNewSegment({ value: '', label: '', color: 'bg-slate-100 text-slate-700' });
      fetchSegments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create segment');
    }
  };

  const handleDeleteSegment = async (segmentId) => {
    if (!confirm('Delete this segment?')) return;
    try {
      const res = await segmentsAPI.delete(segmentId);
      toast.success(res.data.message);
      fetchSegments();
    } catch (error) {
      toast.error('Failed to delete segment');
    }
  };

  const handleCreateProductLine = async (e) => {
    e.preventDefault();
    if (!newProductLine.name) {
      toast.error('Please enter product line name');
      return;
    }
    try {
      await productLinesAPI.create(newProductLine);
      toast.success('Product line created');
      setNewProductLine({ name: '', color: 'bg-slate-100 text-slate-700', description: '' });
      setProductLineDialog(false);
      fetchProductLines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product line');
    }
  };

  const handleDeleteProductLine = async (productLineId) => {
    if (!confirm('Delete this product line?')) return;
    try {
      const res = await productLinesAPI.delete(productLineId);
      toast.success(res.data.message);
      fetchProductLines();
    } catch (error) {
      toast.error('Failed to delete product line');
    }
  };

  const fetchData = async (pageNum = 0, reset = false) => {
    // Prevent duplicate fetches for the same page
    if (!reset && loadingPageRef.current === pageNum) {
      return;
    }
    
    try {
      if (reset) {
        setLoading(true);
        loadingPageRef.current = 0;
      } else {
        setLoadingMore(true);
        loadingPageRef.current = pageNum;
      }
      
      const params = {
        sort_by: sortBy,
        sort_order: sortOrder,
        skip: pageNum * PAGE_SIZE,
        limit: PAGE_SIZE
      };
      
      // Status filtering
      if (statusFilter === 'all_including_disabled') {
        params.include_disabled = true;
      } else if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      if (aiScoreFilter !== 'all') {
        params.ai_score_filter = aiScoreFilter;
      }
      if (productLineFilters.length > 0) {
        params.product_lines = productLineFilters.join(',');
      }
      
      const [contactsRes, companiesRes] = await Promise.all([
        contactsAPI.getAll(params),
        reset ? companiesAPI.getAll() : Promise.resolve({ data: companies })
      ]);
      
      const newContacts = contactsRes.data || [];
      
      if (reset) {
        setContacts(newContacts);
        setCompanies(companiesRes.data || []);
      } else {
        // Only append if we got new unique contacts
        setContacts(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const uniqueNew = newContacts.filter(c => !existingIds.has(c.id));
          return [...prev, ...uniqueNew];
        });
      }
      
      // Check if there are more contacts to load
      setHasMore(newContacts.length === PAGE_SIZE);
      setPage(pageNum);
    } catch (error) {
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      // Reset loading ref after completion
      if (reset) {
        loadingPageRef.current = -1;
      }
    }
  };
  
  // Load more contacts
  const loadMore = () => {
    if (!loadingMore && hasMore && loadingPageRef.current !== page + 1) {
      fetchData(page + 1, false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNewCompanyChange = (field, value) => {
    setNewCompanyData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateInlineCompany = async () => {
    if (!newCompanyData.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    try {
      const res = await companiesAPI.create({
        ...newCompanyData,
        status: 'active',
        key_contact_id: null // Will be updated after contact is created
      });
      toast.success('Company created');
      // Add to companies list and select it
      setCompanies(prev => [...prev, res.data]);
      setFormData(prev => ({ ...prev, company_id: res.data.id }));
      // Reset and hide the form
      setNewCompanyData({ name: '', industry: '', website: '', phone: '', address: '', city: '', country: '' });
      setShowNewCompanyForm(false);
    } catch (error) {
      toast.error('Failed to create company');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Submitting contact form:', formData);
    try {
      if (editingContact) {
        console.log('Updating contact:', editingContact.id);
        await contactsAPI.update(editingContact.id, formData);
        toast.success('Contact updated');
      } else {
        console.log('Creating new contact...');
        const response = await contactsAPI.create(formData);
        console.log('Contact created:', response.data);
        toast.success('Contact created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData(0, true);
    } catch (error) {
      console.error('Contact save error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save contact');
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      phone: contact.phone || '',
      company_id: contact.company_id || '',
      job_title: contact.job_title || '',
      status: contact.status,
      source: contact.source || '',
      notes: contact.notes || '',
      segments: contact.segments || [],
      product_lines: contact.product_lines || [],
      contact_type: contact.contact_type || 'prospect',
      address_street: contact.address_street || '',
      address_city: contact.address_city || '',
      address_state: contact.address_state || '',
      address_zip: contact.address_zip || '',
      address_country: contact.address_country || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await contactsAPI.delete(id);
      toast.success('Contact deleted');
      fetchData(0, true);
    } catch (error) {
      console.error('Delete contact error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete contact');
    }
  };

  // Bulk selection handlers
  const toggleSelectContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(c => c.id));
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedContacts.length === 0) {
      toast.error('No contacts selected');
      return;
    }
    
    const statusLabel = newStatus.charAt(0).toUpperCase() + newStatus.slice(1);
    if (!window.confirm(`Are you sure you want to set ${selectedContacts.length} contact(s) to "${statusLabel}"?`)) {
      return;
    }
    
    setBulkActionLoading(true);
    try {
      const result = await contactsAPI.bulkStatusUpdate(selectedContacts, newStatus);
      toast.success(result.data.message);
      setSelectedContacts([]);
      fetchData(0, true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contacts');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk update contact type (Customer/Prospect)
  const handleBulkContactTypeUpdate = async (newType) => {
    if (selectedContacts.length === 0) {
      toast.error('No contacts selected');
      return;
    }
    
    const typeLabel = newType === 'customer' ? 'Customer' : 'Prospect';
    if (!window.confirm(`Mark ${selectedContacts.length} contact(s) as "${typeLabel}"?`)) {
      return;
    }
    
    setBulkActionLoading(true);
    try {
      // Update each contact's contact_type
      await Promise.all(selectedContacts.map(id => 
        contactsAPI.update(id, { contact_type: newType })
      ));
      toast.success(`${selectedContacts.length} contact(s) marked as ${typeLabel}`);
      setSelectedContacts([]);
      fetchData(0, true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact types');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Auto-detect customers based on order history
  const handleAutoDetectCustomers = async () => {
    if (!window.confirm('This will scan all orders and mark contacts with orders as "Customer". Continue?')) {
      return;
    }
    
    setBulkActionLoading(true);
    try {
      const result = await contactsAPI.autoDetectCustomers();
      toast.success(result.data.message);
      fetchData(0, true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to auto-detect customers');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkAssign = async () => {
    if (selectedContacts.length === 0) {
      toast.error('No contacts selected');
      return;
    }
    
    const hasAssignment = bulkAssignData.assigned_to || 
                          bulkAssignData.segments.length > 0 || 
                          bulkAssignData.product_lines.length > 0;
    
    if (!hasAssignment) {
      toast.error('Please select at least one assignment option');
      return;
    }
    
    setBulkAssignLoading(true);
    try {
      const payload = {
        contact_ids: selectedContacts,
        mode: bulkAssignData.mode
      };
      
      if (bulkAssignData.assigned_to) {
        payload.assigned_to = bulkAssignData.assigned_to;
      }
      if (bulkAssignData.segments.length > 0) {
        payload.segments = bulkAssignData.segments;
      }
      if (bulkAssignData.product_lines.length > 0) {
        payload.product_lines = bulkAssignData.product_lines;
      }
      
      const result = await contactsAPI.bulkAssign(payload);
      toast.success(result.data.message);
      setSelectedContacts([]);
      setBulkAssignDialog(false);
      setBulkAssignData({ assigned_to: '', segments: [], product_lines: [], mode: 'add' });
      fetchData(0, true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign contacts');
    } finally {
      setBulkAssignLoading(false);
    }
  };

  const resetForm = () => {
    setEditingContact(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company_id: '',
      job_title: '',
      status: 'active',
      source: '',
      notes: '',
      segments: [],
      product_lines: [],
      contact_type: 'prospect',
      address_street: '',
      address_city: '',
      address_state: '',
      address_zip: '',
      address_country: ''
    });
    setShowNewCompanyForm(false);
    setNewCompanyData({ name: '', industry: '', website: '', phone: '', address: '', city: '', country: '' });
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || '-';
  };

  const toggleSegmentFilter = (segment) => {
    setSegmentFilters(prev => 
      prev.includes(segment) 
        ? prev.filter(s => s !== segment)
        : [...prev, segment]
    );
  };

  const clearSegmentFilters = () => {
    setSegmentFilters([]);
  };

  const toggleProductLineFilter = (productLineId) => {
    setProductLineFilters(prev => 
      prev.includes(productLineId) 
        ? prev.filter(pl => pl !== productLineId)
        : [...prev, productLineId]
    );
  };

  const clearProductLineFilters = () => {
    setProductLineFilters([]);
  };

  const getProductLineName = (productLineId) => {
    const pl = productLines.find(p => p.id === productLineId);
    return pl?.name || productLineId;
  };

  const getProductLineColor = (productLineId) => {
    const pl = productLines.find(p => p.id === productLineId);
    return pl?.color || 'bg-slate-100 text-slate-700';
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || statusFilter === 'all_including_disabled' || contact.status === statusFilter;
    const matchesSegments = segmentFilters.length === 0 || 
      (contact.segments && segmentFilters.some(sf => contact.segments.includes(sf)));
    // Product line filtering is done server-side, but add client filter for real-time
    const matchesProductLines = productLineFilters.length === 0 || 
      (contact.product_lines && productLineFilters.some(pl => contact.product_lines.includes(pl)));
    return matchesSearch && matchesStatus && matchesSegments && matchesProductLines;
  });

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    inactive: 'bg-amber-100 text-amber-700',
    lead: 'bg-blue-100 text-blue-700',
    prospect: 'bg-purple-100 text-purple-700',
    disabled: 'bg-red-100 text-red-700'
  };

  const getAiScoreColor = (score) => {
    if (score === null || score === undefined) return 'bg-slate-100 text-slate-500';
    if (score >= 80) return 'bg-emerald-100 text-emerald-700';
    if (score >= 50) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  const getAiScoreIcon = (score) => {
    if (score === null || score === undefined) return <Sparkles className="h-3 w-3" />;
    if (score >= 80) return <TrendingUp className="h-3 w-3" />;
    if (score >= 50) return <Minus className="h-3 w-3" />;
    return <TrendingDown className="h-3 w-3" />;
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Click-to-call handler
  const handleClickToCall = async (contact) => {
    if (!contact.phone) {
      toast.error('No phone number available for this contact');
      return;
    }
    
    try {
      const response = await voipAPI.initiateCall({
        contact_id: contact.id,
        contact_name: `${contact.first_name} ${contact.last_name}`,
        contact_phone: contact.phone
      });
      
      if (response.data.success) {
        toast.success(`Calling ${contact.first_name} ${contact.last_name}...`, {
          description: contact.phone
        });
      } else {
        toast.error('Failed to initiate call');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate call');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contacts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight">Contacts</h1>
          <p className="text-muted-foreground mt-1">Manage your customer contacts</p>
        </div>
        <div className="flex gap-2">
          {/* Manage Product Lines Button (Admin Only) */}
          {hasRole('admin') && (
            <Dialog open={productLineDialog} onOpenChange={setProductLineDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="manage-product-lines-btn">
                  <Package className="h-4 w-4 mr-2" />
                  Product Lines
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Manage Product Lines</DialogTitle>
                  <DialogDescription>Create and manage product lines for customer segmentation</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Create New Product Line */}
                  <form onSubmit={handleCreateProductLine} className="space-y-3 p-3 border rounded-lg bg-slate-50">
                    <h4 className="font-medium text-sm">Create New Product Line</h4>
                    <Input
                      placeholder="Product Line Name"
                      value={newProductLine.name}
                      onChange={(e) => setNewProductLine(prev => ({ ...prev, name: e.target.value }))}
                    />
                    <Input
                      placeholder="Description (optional)"
                      value={newProductLine.description}
                      onChange={(e) => setNewProductLine(prev => ({ ...prev, description: e.target.value }))}
                    />
                    <Select value={newProductLine.color} onValueChange={(v) => setNewProductLine(prev => ({ ...prev, color: v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select color" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bg-blue-100 text-blue-700">Blue</SelectItem>
                        <SelectItem value="bg-slate-200 text-slate-700">Silver</SelectItem>
                        <SelectItem value="bg-orange-100 text-orange-700">Orange</SelectItem>
                        <SelectItem value="bg-emerald-100 text-emerald-700">Green</SelectItem>
                        <SelectItem value="bg-purple-100 text-purple-700">Purple</SelectItem>
                        <SelectItem value="bg-amber-100 text-amber-700">Amber</SelectItem>
                        <SelectItem value="bg-red-100 text-red-700">Red</SelectItem>
                        <SelectItem value="bg-pink-100 text-pink-700">Pink</SelectItem>
                        <SelectItem value="bg-cyan-100 text-cyan-700">Cyan</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="submit" size="sm" className="w-full">
                      <Plus className="h-4 w-4 mr-1" /> Add Product Line
                    </Button>
                  </form>
                  
                  {/* Existing Product Lines */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Existing Product Lines ({productLines.length})</h4>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {productLines.map(pl => (
                        <div key={pl.id} className="flex items-center justify-between p-2 border rounded-lg">
                          <div className="flex items-center gap-2">
                            <Badge className={pl.color}>{pl.name}</Badge>
                            {pl.description && <span className="text-xs text-muted-foreground">{pl.description}</span>}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteProductLine(pl.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
          
          {/* Manage Segments Button */}
          <Dialog open={segmentDialog} onOpenChange={setSegmentDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="manage-segments-btn">
                <Settings className="h-4 w-4 mr-2" />
                Manage Segments
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Manage Customer Segments</DialogTitle>
                <DialogDescription>Create and manage customer segments for categorization</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Create New Segment */}
                <form onSubmit={handleCreateSegment} className="space-y-3 p-3 border rounded-lg bg-slate-50">
                  <h4 className="font-medium text-sm">Create New Segment</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Value (e.g., vip)"
                      value={newSegment.value}
                      onChange={(e) => setNewSegment(prev => ({ ...prev, value: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                    />
                    <Input
                      placeholder="Label (e.g., VIP Customer)"
                      value={newSegment.label}
                      onChange={(e) => setNewSegment(prev => ({ ...prev, label: e.target.value }))}
                    />
                  </div>
                  <Select value={newSegment.color} onValueChange={(v) => setNewSegment(prev => ({ ...prev, color: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select color" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bg-blue-100 text-blue-700">Blue</SelectItem>
                      <SelectItem value="bg-emerald-100 text-emerald-700">Green</SelectItem>
                      <SelectItem value="bg-purple-100 text-purple-700">Purple</SelectItem>
                      <SelectItem value="bg-amber-100 text-amber-700">Amber</SelectItem>
                      <SelectItem value="bg-red-100 text-red-700">Red</SelectItem>
                      <SelectItem value="bg-pink-100 text-pink-700">Pink</SelectItem>
                      <SelectItem value="bg-cyan-100 text-cyan-700">Cyan</SelectItem>
                      <SelectItem value="bg-orange-100 text-orange-700">Orange</SelectItem>
                      <SelectItem value="bg-slate-100 text-slate-700">Gray</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button type="submit" size="sm" className="w-full">
                    <Plus className="h-4 w-4 mr-1" /> Add Segment
                  </Button>
                </form>
                
                {/* Existing Segments */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Existing Segments ({customerSegments.length})</h4>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {customerSegments.map(seg => (
                      <div key={seg.id} className="flex items-center justify-between p-2 border rounded-lg">
                        <Badge className={seg.color}>{seg.label}</Badge>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteSegment(seg.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Bulk Actions Dropdown */}
          {selectedContacts.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={bulkActionLoading} data-testid="bulk-actions-btn">
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Bulk Actions ({selectedContacts.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setBulkAssignDialog(true)}>
                  <UserPlus className="h-4 w-4 mr-2 text-primary" />
                  Assign Contacts
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleBulkStatusUpdate('active')}>
                  <UserCheck className="h-4 w-4 mr-2 text-emerald-600" />
                  Set Active
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkStatusUpdate('inactive')}>
                  <UserMinus className="h-4 w-4 mr-2 text-amber-600" />
                  Set Inactive
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkStatusUpdate('lead')}>
                  <User className="h-4 w-4 mr-2 text-blue-600" />
                  Set as Lead
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleBulkContactTypeUpdate('customer')}>
                  <ShoppingCart className="h-4 w-4 mr-2 text-emerald-600" />
                  Mark as Customer
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkContactTypeUpdate('prospect')}>
                  <Target className="h-4 w-4 mr-2 text-amber-600" />
                  Mark as Prospect
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleBulkStatusUpdate('disabled')} className="text-red-600">
                  <UserX className="h-4 w-4 mr-2" />
                  Disable Contacts
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {/* Bulk Assignment Dialog */}
          <Dialog open={bulkAssignDialog} onOpenChange={(open) => {
            setBulkAssignDialog(open);
            if (!open) setBulkAssignData({ assigned_to: '', segments: [], product_lines: [], mode: 'add' });
          }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-primary" />
                  Assign {selectedContacts.length} Contact(s)
                </DialogTitle>
                <DialogDescription>
                  Assign selected contacts to a team member, segments, or product lines.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-5 py-4">
                {/* Assignment Mode */}
                <div className="space-y-3">
                  <Label>Assignment Mode</Label>
                  <RadioGroup 
                    value={bulkAssignData.mode} 
                    onValueChange={(v) => setBulkAssignData(p => ({ ...p, mode: v }))}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="add" id="mode-add" />
                      <Label htmlFor="mode-add" className="font-normal cursor-pointer">
                        Add to existing
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="replace" id="mode-replace" />
                      <Label htmlFor="mode-replace" className="font-normal cursor-pointer">
                        Replace existing
                      </Label>
                    </div>
                  </RadioGroup>
                  <p className="text-xs text-muted-foreground">
                    {bulkAssignData.mode === 'add' 
                      ? 'New assignments will be added to existing ones' 
                      : 'Existing assignments will be replaced with new ones'}
                  </p>
                </div>

                {/* Assign to Team Member */}
                <div className="space-y-2">
                  <Label>Assign to Team Member</Label>
                  <Select
                    value={bulkAssignData.assigned_to}
                    onValueChange={(v) => setBulkAssignData(p => ({ ...p, assigned_to: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="bulk-assign-user-select">
                      <SelectValue placeholder="Select team member..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No change</SelectItem>
                      {teamMembers.map(member => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.full_name || member.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Assign Segments */}
                <div className="space-y-2">
                  <Label>Assign Segments</Label>
                  <div className="flex flex-wrap gap-2 p-3 border rounded-lg min-h-[60px]">
                    {customerSegments.map(segment => (
                      <Badge
                        key={segment.value}
                        variant={bulkAssignData.segments.includes(segment.value) ? 'default' : 'outline'}
                        className="cursor-pointer transition-colors"
                        onClick={() => {
                          setBulkAssignData(p => ({
                            ...p,
                            segments: p.segments.includes(segment.value)
                              ? p.segments.filter(s => s !== segment.value)
                              : [...p.segments, segment.value]
                          }));
                        }}
                      >
                        <Tags className="h-3 w-3 mr-1" />
                        {segment.label}
                      </Badge>
                    ))}
                    {customerSegments.length === 0 && (
                      <p className="text-sm text-muted-foreground">No segments defined</p>
                    )}
                  </div>
                </div>

                {/* Assign Product Lines */}
                <div className="space-y-2">
                  <Label>Assign Product Lines</Label>
                  <div className="flex flex-wrap gap-2 p-3 border rounded-lg min-h-[60px]">
                    {productLines.map(pl => (
                      <Badge
                        key={pl.id}
                        variant={bulkAssignData.product_lines.includes(pl.id) ? 'default' : 'outline'}
                        className="cursor-pointer transition-colors"
                        onClick={() => {
                          setBulkAssignData(p => ({
                            ...p,
                            product_lines: p.product_lines.includes(pl.id)
                              ? p.product_lines.filter(id => id !== pl.id)
                              : [...p.product_lines, pl.id]
                          }));
                        }}
                      >
                        <Package className="h-3 w-3 mr-1" />
                        {pl.name}
                      </Badge>
                    ))}
                    {productLines.length === 0 && (
                      <p className="text-sm text-muted-foreground">No product lines defined</p>
                    )}
                  </div>
                </div>

                {/* Summary */}
                {(bulkAssignData.assigned_to || bulkAssignData.segments.length > 0 || bulkAssignData.product_lines.length > 0) && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <p className="text-sm font-medium text-blue-900 mb-1">Assignment Summary</p>
                    <ul className="text-sm text-blue-800 space-y-1">
                      {bulkAssignData.assigned_to && (
                        <li>• Assign to: {teamMembers.find(m => m.id === bulkAssignData.assigned_to)?.full_name || 'Selected member'}</li>
                      )}
                      {bulkAssignData.segments.length > 0 && (
                        <li>• Segments: {bulkAssignData.segments.map(s => customerSegments.find(seg => seg.value === s)?.label || s).join(', ')}</li>
                      )}
                      {bulkAssignData.product_lines.length > 0 && (
                        <li>• Product Lines: {bulkAssignData.product_lines.map(id => productLines.find(pl => pl.id === id)?.name || id).join(', ')}</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkAssignDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleBulkAssign} disabled={bulkAssignLoading} data-testid="bulk-assign-submit-btn">
                  {bulkAssignLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Assign Contacts
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Add Contact Dialog */}
          {hasRole('admin') && (
            <Button 
              variant="outline" 
              onClick={handleAutoDetectCustomers}
              disabled={bulkActionLoading}
              data-testid="auto-detect-customers-btn"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Auto-Detect Customers
            </Button>
          )}
          <Link to="/contacts/import-export">
            <Button variant="outline" data-testid="import-export-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Import/Export
            </Button>
          </Link>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-contact-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle>{editingContact ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
                <DialogDescription>
                  {editingContact ? 'Update contact information' : 'Create a new contact record'}
                </DialogDescription>
              </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    required
                    data-testid="contact-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    required
                    data-testid="contact-lastname-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  data-testid="contact-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  data-testid="contact-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                {!showNewCompanyForm ? (
                  <div className="space-y-2">
                    <Select value={formData.company_id || "none"} onValueChange={(v) => {
                      if (v === "create_new") {
                        setShowNewCompanyForm(true);
                      } else {
                        handleChange('company_id', v === "none" ? "" : v);
                      }
                    }}>
                      <SelectTrigger data-testid="contact-company-select">
                        <SelectValue placeholder="Select company" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="create_new" className="text-blue-600 font-medium">
                          + Create New Company
                        </SelectItem>
                        {companies.map(company => (
                          <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="border rounded-lg p-3 bg-slate-50 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-blue-700">New Company Details</Label>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setShowNewCompanyForm(false);
                          setNewCompanyData({ name: '', industry: '', website: '', phone: '', address: '', city: '', country: '' });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Company Name *"
                        value={newCompanyData.name}
                        onChange={(e) => handleNewCompanyChange('name', e.target.value)}
                        data-testid="new-company-name"
                      />
                      <Input
                        placeholder="Industry"
                        value={newCompanyData.industry}
                        onChange={(e) => handleNewCompanyChange('industry', e.target.value)}
                        data-testid="new-company-industry"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Website"
                        value={newCompanyData.website}
                        onChange={(e) => handleNewCompanyChange('website', e.target.value)}
                        data-testid="new-company-website"
                      />
                      <Input
                        placeholder="Phone"
                        value={newCompanyData.phone}
                        onChange={(e) => handleNewCompanyChange('phone', e.target.value)}
                        data-testid="new-company-phone"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder="Address"
                        value={newCompanyData.address}
                        onChange={(e) => handleNewCompanyChange('address', e.target.value)}
                        data-testid="new-company-address"
                      />
                      <Input
                        placeholder="City"
                        value={newCompanyData.city}
                        onChange={(e) => handleNewCompanyChange('city', e.target.value)}
                        data-testid="new-company-city"
                      />
                      <Input
                        placeholder="Country"
                        value={newCompanyData.country}
                        onChange={(e) => handleNewCompanyChange('country', e.target.value)}
                        data-testid="new-company-country"
                      />
                    </div>
                    <Button 
                      type="button" 
                      size="sm" 
                      onClick={handleCreateInlineCompany}
                      data-testid="create-inline-company-btn"
                    >
                      Create Company
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="job_title">Job Title</Label>
                <Input
                  id="job_title"
                  value={formData.job_title}
                  onChange={(e) => handleChange('job_title', e.target.value)}
                  data-testid="contact-jobtitle-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                  <SelectTrigger data-testid="contact-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="disabled">
                      <span className="text-red-600">Disabled</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={3}
                  data-testid="contact-notes-input"
                />
              </div>
              
              {/* Contact Type */}
              <div className="space-y-2">
                <Label>Contact Type</Label>
                <Select value={formData.contact_type || 'prospect'} onValueChange={(value) => handleChange('contact_type', value)}>
                  <SelectTrigger data-testid="contact-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Address Section */}
              <div className="space-y-3 pt-2 border-t">
                <Label className="text-base font-semibold">Address</Label>
                <div className="space-y-2">
                  <Input
                    placeholder="Street Address"
                    value={formData.address_street || ''}
                    onChange={(e) => handleChange('address_street', e.target.value)}
                    data-testid="contact-address-street"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="City"
                    value={formData.address_city || ''}
                    onChange={(e) => handleChange('address_city', e.target.value)}
                    data-testid="contact-address-city"
                  />
                  <Input
                    placeholder="State/Province"
                    value={formData.address_state || ''}
                    onChange={(e) => handleChange('address_state', e.target.value)}
                    data-testid="contact-address-state"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="ZIP/Postal Code"
                    value={formData.address_zip || ''}
                    onChange={(e) => handleChange('address_zip', e.target.value)}
                    data-testid="contact-address-zip"
                  />
                  <Input
                    placeholder="Country"
                    value={formData.address_country || ''}
                    onChange={(e) => handleChange('address_country', e.target.value)}
                    data-testid="contact-address-country"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Product Lines</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-lg">
                  {productLines.map(pl => (
                    <Badge
                      key={pl.id}
                      variant={formData.product_lines?.includes(pl.id) ? 'default' : 'outline'}
                      className={`cursor-pointer ${formData.product_lines?.includes(pl.id) ? pl.color : 'bg-white hover:bg-slate-50'}`}
                      onClick={() => {
                        const current = formData.product_lines || [];
                        const updated = current.includes(pl.id)
                          ? current.filter(p => p !== pl.id)
                          : [...current, pl.id];
                        handleChange('product_lines', updated);
                      }}
                    >
                      {pl.name}
                    </Badge>
                  ))}
                  {productLines.length === 0 && (
                    <span className="text-sm text-muted-foreground">No product lines available</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Click to select/deselect product lines</p>
              </div>
              <div className="space-y-2">
                <Label>Segments</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-lg">
                  {customerSegments.map(segment => (
                    <Badge
                      key={segment.value}
                      variant={formData.segments?.includes(segment.value) ? 'default' : 'outline'}
                      className={`cursor-pointer ${formData.segments?.includes(segment.value) ? segment.color : 'bg-white hover:bg-slate-50'}`}
                      onClick={() => {
                        const current = formData.segments || [];
                        const updated = current.includes(segment.value)
                          ? current.filter(s => s !== segment.value)
                          : [...current, segment.value];
                        handleChange('segments', updated);
                      }}
                    >
                      {segment.label}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Click to select/deselect segments</p>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="contact-submit-btn">
                  {editingContact ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="contact-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]" data-testid="contact-filter-status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className="flex items-center gap-2">
                      {opt.color && <span className={`w-2 h-2 rounded-full ${opt.color.split(' ')[0]}`}></span>}
                      {opt.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* AI Score Filter */}
            <Select value={aiScoreFilter} onValueChange={setAiScoreFilter}>
              <SelectTrigger className="w-[160px]" data-testid="contact-filter-ai-score">
                <Sparkles className="h-4 w-4 mr-2 text-purple-500" />
                <SelectValue placeholder="AI Score" />
              </SelectTrigger>
              <SelectContent>
                {AI_SCORE_FILTERS.map(filter => (
                  <SelectItem key={filter.value} value={filter.value}>
                    <span className="flex items-center gap-2">
                      {filter.icon && <filter.icon className={`h-3 w-3 ${filter.color}`} />}
                      {filter.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Segment Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[150px] justify-start" data-testid="segment-filter-btn">
                  <Filter className="h-4 w-4 mr-2" />
                  {segmentFilters.length > 0 
                    ? `${segmentFilters.length} Segment${segmentFilters.length > 1 ? 's' : ''}`
                    : 'Segments'
                  }
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="end">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">Filter by Segment</span>
                    {segmentFilters.length > 0 && (
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={clearSegmentFilters}>
                        Clear all
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {customerSegments.map(segment => (
                      <div 
                        key={segment.value}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={`filter-${segment.value}`}
                          checked={segmentFilters.includes(segment.value)}
                          onCheckedChange={() => toggleSegmentFilter(segment.value)}
                        />
                        <label 
                          htmlFor={`filter-${segment.value}`}
                          className="text-sm flex items-center gap-2 cursor-pointer"
                        >
                          <Badge className={segment.color} variant="outline">
                            {segment.label}
                          </Badge>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Product Lines Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[150px] justify-start" data-testid="product-line-filter-btn">
                  <Package className="h-4 w-4 mr-2" />
                  {productLineFilters.length > 0 
                    ? `${productLineFilters.length} Product${productLineFilters.length > 1 ? 's' : ''}`
                    : 'Product Lines'
                  }
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="end">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">Filter by Product Line</span>
                    {productLineFilters.length > 0 && (
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={clearProductLineFilters}>
                        Clear all
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {productLines.map(pl => (
                      <div 
                        key={pl.id}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={`filter-pl-${pl.id}`}
                          checked={productLineFilters.includes(pl.id)}
                          onCheckedChange={() => toggleProductLineFilter(pl.id)}
                        />
                        <label 
                          htmlFor={`filter-pl-${pl.id}`}
                          className="text-sm flex items-center gap-2 cursor-pointer"
                        >
                          <Badge className={pl.color} variant="outline">
                            {pl.name}
                          </Badge>
                        </label>
                      </div>
                    ))}
                    {productLines.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-2">No product lines available</p>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Sort */}
            <Select value={`${sortBy}-${sortOrder}`} onValueChange={(v) => {
              const [field, order] = v.split('-');
              setSortBy(field);
              setSortOrder(order);
            }}>
              <SelectTrigger className="w-[180px]" data-testid="contact-sort">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at-desc">Newest First</SelectItem>
                <SelectItem value="created_at-asc">Oldest First</SelectItem>
                <SelectItem value="ai_health_score-desc">AI Score (High to Low)</SelectItem>
                <SelectItem value="ai_health_score-asc">AI Score (Low to High)</SelectItem>
                <SelectItem value="first_name-asc">Name (A-Z)</SelectItem>
                <SelectItem value="first_name-desc">Name (Z-A)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Active Filters */}
          {(segmentFilters.length > 0 || aiScoreFilter !== 'all' || productLineFilters.length > 0) && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              <div className="flex flex-wrap gap-1">
                {aiScoreFilter !== 'all' && (
                  <Badge 
                    className="bg-purple-100 text-purple-700 cursor-pointer"
                    onClick={() => setAiScoreFilter('all')}
                  >
                    AI: {AI_SCORE_FILTERS.find(f => f.value === aiScoreFilter)?.label}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                )}
                {segmentFilters.map(sf => {
                  const seg = customerSegments.find(s => s.value === sf);
                  return (
                    <Badge 
                      key={sf} 
                      className={`${seg?.color || 'bg-slate-100'} cursor-pointer`}
                      onClick={() => toggleSegmentFilter(sf)}
                    >
                      {seg?.label || sf}
                      <X className="h-3 w-3 ml-1" />
                    </Badge>
                  );
                })}
                {productLineFilters.map(plId => (
                  <Badge 
                    key={plId} 
                    className={`${getProductLineColor(plId)} cursor-pointer`}
                    onClick={() => toggleProductLineFilter(plId)}
                  >
                    {getProductLineName(plId)}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-contacts"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Portal</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>AI Score</TableHead>
                <TableHead>Product Lines</TableHead>
                <TableHead>Segments</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContacts.length > 0 ? (
                filteredContacts.map((contact) => (
                  <TableRow 
                    key={contact.id} 
                    className={`data-table-row ${contact.status === 'disabled' ? 'opacity-50 bg-slate-50' : ''} ${selectedContacts.includes(contact.id) ? 'bg-blue-50' : ''}`} 
                    data-testid={`contact-row-${contact.id}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedContacts.includes(contact.id)}
                        onCheckedChange={() => toggleSelectContact(contact.id)}
                        data-testid={`select-contact-${contact.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`h-9 w-9 rounded-full flex items-center justify-center ${contact.status === 'disabled' ? 'bg-red-100' : 'bg-slate-100'}`}>
                          <User className={`h-4 w-4 ${contact.status === 'disabled' ? 'text-red-600' : 'text-slate-600'}`} />
                        </div>
                        <div>
                          <Link 
                            to={`/contacts/${contact.id}`}
                            className={`font-medium hover:underline transition-colors ${contact.status === 'disabled' ? 'text-slate-500' : 'text-slate-900 hover:text-blue-600'}`}
                            data-testid={`contact-name-link-${contact.id}`}
                          >
                            {contact.first_name} {contact.last_name}
                            {contact.status === 'disabled' && <span className="ml-2 text-xs text-red-500">(Disabled)</span>}
                          </Link>
                          <div className="flex items-center gap-2 mt-1">
                            {/* Customer/Prospect Badge */}
                            <Badge 
                              variant="outline" 
                              className={contact.contact_type === 'customer' 
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 text-xs' 
                                : 'bg-amber-50 text-amber-700 border-amber-200 text-xs'}
                              data-testid={`contact-type-badge-${contact.id}`}
                            >
                              {contact.contact_type === 'customer' ? 'Customer' : 'Prospect'}
                            </Badge>
                            {contact.job_title && (
                              <span className="text-xs text-muted-foreground">{contact.job_title}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="truncate max-w-[180px]">{contact.email}</span>
                      </div>
                      {/* Show city/state if available */}
                      {(contact.address_city || contact.address_state) && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {[contact.address_city, contact.address_state].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.phone ? (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span>{contact.phone}</span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => handleClickToCall(contact)}
                                  data-testid={`call-contact-${contact.id}`}
                                >
                                  <PhoneCall className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Click to call</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {contact.company_id ? (
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          {getCompanyName(contact.company_id)}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {contact.has_portal_access ? (
                        <Badge className="bg-emerald-100 text-emerald-700" data-testid={`portal-access-${contact.id}`}>
                          <Globe className="h-3 w-3 mr-1" />
                          Yes
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500" data-testid={`portal-access-${contact.id}`}>
                          No
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {contact.created_at ? format(new Date(contact.created_at), 'MMM d, yyyy') : '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getAiScoreColor(contact.ai_health_score)} flex items-center gap-1 w-fit`}>
                        {getAiScoreIcon(contact.ai_health_score)}
                        {contact.ai_health_score !== null && contact.ai_health_score !== undefined 
                          ? contact.ai_health_score 
                          : 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {contact.product_lines && contact.product_lines.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {contact.product_lines.slice(0, 2).map(plId => (
                            <Badge key={plId} className={getProductLineColor(plId)} variant="outline">
                              {getProductLineName(plId)}
                            </Badge>
                          ))}
                          {contact.product_lines.length > 2 && (
                            <Badge variant="outline" className="bg-slate-100">
                              +{contact.product_lines.length - 2}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.segments && contact.segments.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {contact.segments.slice(0, 2).map(s => {
                            const seg = customerSegments.find(cs => cs.value === s);
                            return (
                              <Badge key={s} className={seg?.color || 'bg-slate-100'} variant="outline">
                                {seg?.label || s}
                              </Badge>
                            );
                          })}
                          {contact.segments.length > 2 && (
                            <Badge variant="outline" className="bg-slate-100">
                              +{contact.segments.length - 2}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[contact.status]}>
                        {contact.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                          data-testid={`view-contact-${contact.id}`}
                        >
                          <Link to={`/contacts/${contact.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(contact);
                          }}
                          data-testid={`edit-contact-${contact.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(contact.id);
                          }}
                          data-testid={`delete-contact-${contact.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                    No contacts found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          
          {/* Load More / Infinite Scroll */}
          {contacts.length > 0 && (
            <div className="flex justify-center py-6 border-t">
              {loadingMore ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Loading more contacts...</span>
                </div>
              ) : hasMore ? (
                <Button variant="outline" onClick={loadMore} data-testid="load-more-contacts">
                  <Plus className="h-4 w-4 mr-2" />
                  Load More Contacts
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground">
                  All {contacts.length} contacts loaded
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactsPage;
